import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { ROLE_LABELS, BACKEND_ASSIGNABLE_ROLES, STATE_STYLE, SEGMENT_STYLE, fmtDate } from "../lib/states";
import { Shuffle, Loader2, CheckSquare, Square, ListChecks, Zap } from "lucide-react";

export default function BulkAssign() {
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [mode, setMode] = useState("quick");

  // quick mode
  const [pool, setPool] = useState("Fresh");
  const [count, setCount] = useState(20);
  const [order, setOrder] = useState("random");
  const [qUser, setQUser] = useState("");
  const [busy, setBusy] = useState(false);

  // manual mode
  const [mUser, setMUser] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [poolFilter, setPoolFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [mBusy, setMBusy] = useState(false);

  const load = () => {
    api.get("/users").then((r) => {
      const callers = r.data.filter((u) => BACKEND_ASSIGNABLE_ROLES.includes(u.role) && u.active !== false);
      setUsers(callers);
      if (callers[0]) { setQUser(callers[0].id); setMUser(callers[0].id); }
    });
    api.get("/leads").then((r) => setLeads(r.data));
  };
  useEffect(load, []);

  const unassigned = leads.filter((l) => !l.assignee_name && !l.is_hot && !["Deal Won", "Deal Lost", "Junk", "Out of Station"].includes(l.state));
  const counts = {
    Fresh: unassigned.filter((l) => l.state === "Fresh").length,
    RNR: unassigned.filter((l) => l.state === "RNR").length,
  };

  const manualList = unassigned
    .filter((l) => poolFilter === "all" || l.state === poolFilter)
    .filter((l) => !search || l.name?.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search));

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = manualList.length > 0 && manualList.every((l) => selected.has(l.id));
  const toggleAll = () => setSelected((prev) => {
    if (allSelected) { const n = new Set(prev); manualList.forEach((l) => n.delete(l.id)); return n; }
    const n = new Set(prev); manualList.forEach((l) => n.add(l.id)); return n;
  });

  const assignQuick = async () => {
    if (!qUser) return toast.error("Pick a team member first");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-assign", { pool, count: Number(count), user_id: qUser, order });
      toast.success(`Assigned ${data.assigned} ${pool} lead(s) to ${data.user}.`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const assignManual = async () => {
    if (!mUser) return toast.error("Pick a team member first");
    if (selected.size === 0) return toast.error("Select at least one lead");
    setMBusy(true);
    try {
      const { data } = await api.post("/leads/bulk-assign-ids", { lead_ids: [...selected], user_id: mUser });
      toast.success(`Assigned ${data.assigned} lead(s) to ${data.user}.`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setMBusy(false); }
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Bulk Assign</h1>
          <p className="text-xs text-stone-500">Distribute unassigned leads to your calling team</p>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Mode switch */}
        <div className="inline-flex rounded-lg border border-stone-200 bg-white p-1">
          <button onClick={() => setMode("quick")} data-testid="mode-quick"
            className={`flex items-center gap-2 text-sm px-4 py-1.5 rounded-md transition-colors ${mode === "quick" ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900"}`}>
            <Zap className="h-4 w-4" /> Quick batch
          </button>
          <button onClick={() => setMode("manual")} data-testid="mode-manual"
            className={`flex items-center gap-2 text-sm px-4 py-1.5 rounded-md transition-colors ${mode === "manual" ? "bg-stone-900 text-white" : "text-stone-600 hover:text-stone-900"}`}>
            <ListChecks className="h-4 w-4" /> Pick manually
          </button>
        </div>

        {mode === "quick" ? (
          <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5 max-w-xl">
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-2">Pool</p>
              <div className="grid grid-cols-2 gap-2">
                {["Fresh", "RNR"].map((p) => (
                  <button key={p} onClick={() => setPool(p)} data-testid={`pool-${p}`}
                    className={`py-3 rounded-md border text-sm font-medium transition-colors ${pool === p ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-700 hover:border-stone-400"}`}>
                    {p} <span className="opacity-70">· {counts[p]} unassigned</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-2">Order</p>
              <div className="grid grid-cols-2 gap-2">
                {[["random", "Random"], ["oldest", "Oldest first"]].map(([v, label]) => (
                  <button key={v} onClick={() => setOrder(v)} data-testid={`order-${v}`}
                    className={`py-2.5 rounded-md border text-sm font-medium transition-colors ${order === v ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-700 hover:border-stone-400"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-2">How many</p>
              <input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} data-testid="assign-count"
                className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-400 mb-2">Assign to</p>
              <select value={qUser} onChange={(e) => setQUser(e.target.value)} data-testid="assign-user"
                className="w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
                {users.length === 0 && <option value="">No team members — create one in Users</option>}
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role]})</option>)}
              </select>
            </div>
            <button onClick={assignQuick} disabled={busy || !qUser} data-testid="assign-submit"
              className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60 transition-colors">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Shuffle className="h-4 w-4" /> Assign {order} batch</>}
            </button>
            <p className="text-xs text-stone-400 leading-relaxed">Follow-up Requested leads stay with their original owner and are never bulk-reassigned.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
            {/* Manual toolbar */}
            <div className="p-4 border-b border-stone-200 flex flex-wrap items-center gap-3">
              <select value={poolFilter} onChange={(e) => setPoolFilter(e.target.value)} data-testid="manual-pool-filter"
                className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                <option value="all">All unassigned</option>
                <option value="Fresh">Fresh</option>
                <option value="RNR">RNR</option>
              </select>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name/phone…" data-testid="manual-search"
                className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white flex-1 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
              <select value={mUser} onChange={(e) => setMUser(e.target.value)} data-testid="manual-user"
                className="border border-stone-300 rounded-md px-3 py-2 text-sm bg-white">
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role]})</option>)}
              </select>
              <button onClick={assignManual} disabled={mBusy || selected.size === 0} data-testid="manual-assign-submit"
                className="bg-stone-900 hover:bg-stone-800 text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 disabled:opacity-50 transition-colors">
                {mBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Assign {selected.size} selected</>}
              </button>
            </div>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-[0.15em] uppercase text-stone-400 border-b border-stone-200">
                  <th className="px-4 py-2.5 w-10">
                    <button onClick={toggleAll} data-testid="manual-select-all" className="text-stone-500 hover:text-stone-900">
                      {allSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </button>
                  </th>
                  <th className="px-4 py-2.5">Lead</th>
                  <th className="px-4 py-2.5">Stage</th>
                  <th className="px-4 py-2.5">Segment</th>
                  <th className="px-4 py-2.5">Arrived</th>
                </tr>
              </thead>
              <tbody data-testid="manual-leads">
                {manualList.map((l) => (
                  <tr key={l.id} onClick={() => toggle(l.id)} data-testid={`manual-row-${l.id}`}
                    className={`border-b border-stone-100 cursor-pointer ${selected.has(l.id) ? "bg-stone-100" : "hover:bg-stone-50"}`}>
                    <td className="px-4 py-2.5">{selected.has(l.id) ? <CheckSquare className="h-4 w-4 text-stone-900" /> : <Square className="h-4 w-4 text-stone-300" />}</td>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-stone-800">{l.name}</p>
                      <p className="text-xs text-stone-400">+91 {l.phone}</p>
                    </td>
                    <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full border ${STATE_STYLE[l.state]?.badge}`}>{l.state}</span></td>
                    <td className="px-4 py-2.5"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${SEGMENT_STYLE[l.segment] || SEGMENT_STYLE.Unknown}`}>{l.segment || "Unknown"}</span></td>
                    <td className="px-4 py-2.5 text-stone-500">{fmtDate(l.created_at)}</td>
                  </tr>
                ))}
                {manualList.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-400">No unassigned leads here.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
